import React, { useEffect, useMemo, useRef, useCallback } from "react";
import {
  useMultiPageLessonStore,
  useCurrentPage,
  useCurrentPageIndex,
  useTotalPages,
  usePageByIndex,
  useBlock,
} from "../store/multiPageLessonStore";
import { useBlocksSubscription } from "../context/SprintWebSocketContext";
import { Block } from "../../types/sprintLessonTypes";
import { PageRenderer } from "./pages/OriginalLessonPage";

// Helper function to extract field name from block index_key or field_path
const getFieldName = (block: Block): string => {
  const key = block.index_key;
  return key.includes(".") ? key.split(".").slice(1).join(".") : key;
};

// Helper function to get page index from block index_key
// Returns the page index or null if it can't be determined
const getPageIndex = (
  block: Block,
  schemaFieldOrder: string[]
): number | null => {
  if (!block.index_key) {
    // No index_key, we can't determine the page
    return null;
  }

  // Extract the container name from the index_key (everything before the first dot)
  const pathParts = block.index_key.split(".");
  const containerName = pathParts[0];

  if (!containerName) {
    // Empty container name
    return null;
  }

  // Look up the container's index in schemaFieldOrder
  const foundPageIndex = schemaFieldOrder.indexOf(containerName);

  if (foundPageIndex !== -1) {
    return foundPageIndex;
  } else {
    // Log warning in development if a container name doesn't match schema
    if (process.env.NODE_ENV !== "production") {
      console.warn(`Unknown page container in index_key: ${containerName}`);
    }
    return null; // Container name doesn't match any known schema field
  }
};

// Helper function to group blocks by page
const groupBlocksByPage = (blocks: Block[], schemaFieldOrder: string[]) => {
  const blocksByPage = new Map<number, Block[]>();
  const currentBlocksMap = new Map<number, Set<string>>();
  const pagesInCurrentUpdate = new Set<number>();

  blocks.forEach((block) => {
    // Get page index using our helper - might return null if page can't be determined
    const pageIndex = getPageIndex(block, schemaFieldOrder);

    // Skip blocks that don't have a valid page index
    if (pageIndex === null) {
      console.log(
        `Skipping block with ID ${block.block_id} - couldn't determine page`
      );
      return;
    }

    // Track that this page appeared in the update
    pagesInCurrentUpdate.add(pageIndex);

    // Initialize collections if needed
    if (!blocksByPage.has(pageIndex)) {
      blocksByPage.set(pageIndex, []);
      currentBlocksMap.set(pageIndex, new Set<string>());
    }

    // Add block to its page collection
    blocksByPage.get(pageIndex)!.push(block);

    // Track field name for stale detection
    const fieldName = getFieldName(block);
    currentBlocksMap.get(pageIndex)!.add(fieldName);
  });

  return { blocksByPage, currentBlocksMap, pagesInCurrentUpdate };
};

// Main MultiPageLesson component
interface MultiPageLessonProps {
  lessonSchema?: any; // JSON schema of the lesson structure
}

export const MultiPageLesson: React.FC<MultiPageLessonProps> = ({
  lessonSchema,
}) => {
  const { setLessonSchema, updatePageBlocks, reset } =
    useMultiPageLessonStore();

  // Use our optimized hooks with memoized selectors
  const currentPageIndex = useCurrentPageIndex();
  const totalPages = useTotalPages();

  // Only fetch the page type from the current page to minimize re-renders
  const currentPage = useCurrentPage();
  
  // Keep track of previous blocks to detect stale blocks using useRef instead of useState
  // since we're mutating the Map directly and don't need React to track state changes
  const previousBlocksByPage = useRef<Map<number, Map<string, string>>>(
    new Map()
  );

  // Memoize the handler function to prevent unnecessary re-subscriptions
  const handleBlocksUpdate = useCallback(
    (blocks: Block[]) => {
      // IMPORTANT: Cache the schema field order once, outside the loop
      // This fixes the critical performance issue of calling getState() for each block
      const { schemaFieldOrder } = useMultiPageLessonStore.getState();

      // Group blocks by page using our helper function (now at module level)
      const { blocksByPage } = groupBlocksByPage(blocks, schemaFieldOrder);

      // Simply update each page with its blocks - no stale detection
      blocksByPage.forEach((pageBlocks, pageIndex) => {
        // Just add/update blocks without removing any
        // The backend is responsible for sending explicit delete commands if needed
        updatePageBlocks(pageIndex, pageBlocks);

        // No need to track previous blocks since we're not doing stale detection
      });
    },
    [updatePageBlocks]
  );

  // The useBlocksSubscription hook now handles its own subscription lifecycle internally
  // We just need to call it once at the top level with our callback
  useBlocksSubscription(handleBlocksUpdate);

  // Use effect to handle cleanup of tracking data on unmount
  useEffect(() => {
    return () => {
      // Clear block tracking to prevent memory leaks
      previousBlocksByPage.current.clear();
    };
  }, []); // Empty dependency array since we only need to clean up on unmount

  if (totalPages === 0) {
    return (
      <div>
        <div>
          <div></div>
          <p>Loading lesson...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Progress indicator */}
      <div>
        <div>
          <h2>
            Page {currentPageIndex + 1} of {totalPages}
          </h2>
          {/* <span className="text-sm text-gray-600">
            {currentPage?.pageType === "read_only_content"
              ? "Reading"
              : "Activity"}
          </span> */}
        </div>
        <div>
          <div
            style={{ width: `${((currentPageIndex + 1) / totalPages) * 100}%` }}
          />
        </div>
      </div>

      {/* Send to appropriate Page type */}
      <div>
        {currentPage?.pageType === "read_only_content" ||
        currentPage?.pageType === "one_user_activity" ? (
          <PageRenderer pageIndex={currentPageIndex} />
        ) : (
          <div>
            <p>
              Page type {currentPage?.pageType} is not supported. Please contact
              support and we'll get it fixed!
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
